import type { KkmServerResponse } from '@/shared/api';

import { optionalPositiveInteger, optionalString } from '@/shared/api';
import { parseInstant, parseLocalInstant, serializeInstant, type Temporal } from '@shared/lib/date-time';

export interface KkmFiscalDocument {
  fiscalDocumentNumber: number;
  fiscalDocumentSign: string;
  fiscalDriveNumber: string;
  fiscalizedAt: string;
  receiptNumber: number;
  receiptUrl: string | null;
  shiftNumber: number;
}

export type KkmFiscalDocumentDraft = {
  [Field in keyof KkmFiscalDocument]: KkmFiscalDocument[Field] | null;
};

export interface KkmFiscalQrCode {
  /** `i` — номер фискального документа. */
  fiscalDocumentNumber: number;
  /** `fp` — фискальный признак документа. */
  fiscalDocumentSign: string;
  /** `fn` — номер фискального накопителя. */
  fiscalDriveNumber: string;
  /** `t` — время ККТ, приведённое к тому же ISO-с-оффсетом контракту, что и `DateTimeKKT`. */
  fiscalizedAt: string;
  /** `n` — признак расчёта. */
  operationType: number | null;
  /** `s` — сумма расчёта; остаётся строкой, чтобы не терять копейки на float. */
  totalAmount: string | null;
}

/**
 * Шаблоны совпадают с `OrderFiscalResultValidator::fiscalDocument()`: разобранные из QR
 * значения обязаны проходить backend-валидацию без послаблений.
 */
const FISCAL_DRIVE_NUMBER_PATTERN = /^[0-9]{8,32}$/;
const FISCAL_DOCUMENT_SIGN_PATTERN = /^[0-9]{4,32}$/;

const REQUIRED_FISCAL_DOCUMENT_FIELDS = [
  'fiscalDriveNumber',
  'fiscalDocumentNumber',
  'fiscalDocumentSign',
  'shiftNumber',
  'receiptNumber',
  'fiscalizedAt',
] as const satisfies ReadonlyArray<keyof KkmFiscalDocument>;

/**
 * QR-payload ФНС: `t=20260820T1200&s=1500.00&fn=<ФН>&i=<ФД>&fp=<ФП>&n=1`.
 *
 * Разбор строгий и без частичного результата: неполный, битый или обрезанный QR даёт
 * `null`, а не документ с дырами. Установленный sample `RegisterCheck` не обещает
 * отдельных полей `FN_Number`, `DocumentNumber`, `FiscalSign` и `DateTimeKKT` — QR
 * остаётся единственным источником реквизитов, поэтому доверять ему можно только целиком.
 */
export function parseKkmFiscalQrCode(value: string, timezone?: string): KkmFiscalQrCode | null {
  const fields = qrFields(value);

  if (fields === null) {
    return null;
  }

  const fiscalDriveNumber = fields.get('fn');
  const fiscalDocumentSign = fields.get('fp');
  const fiscalDocumentNumber = optionalPositiveInteger(fields.get('i'));
  const fiscalizedAt = qrFiscalizedAt(fields.get('t'), timezone);

  if (
    fiscalDriveNumber === undefined ||
    !FISCAL_DRIVE_NUMBER_PATTERN.test(fiscalDriveNumber) ||
    fiscalDocumentSign === undefined ||
    !FISCAL_DOCUMENT_SIGN_PATTERN.test(fiscalDocumentSign) ||
    fiscalDocumentNumber === null ||
    fiscalizedAt === null
  ) {
    return null;
  }

  const rawTotalAmount = fields.get('s');
  const rawOperationType = fields.get('n');
  const totalAmount = rawTotalAmount === undefined ? null : qrDecimal(rawTotalAmount);
  const operationType = rawOperationType === undefined ? null : optionalPositiveInteger(rawOperationType);

  if (
    (rawTotalAmount !== undefined && totalAmount === null) ||
    (rawOperationType !== undefined && operationType === null)
  ) {
    return null;
  }

  return {
    fiscalDocumentNumber,
    fiscalDocumentSign,
    fiscalDriveNumber,
    fiscalizedAt,
    operationType,
    totalAmount,
  };
}

export function extractKkmFiscalDocument(response: KkmServerResponse, timezone?: string): KkmFiscalDocument | null {
  const draft = extractKkmFiscalDocumentDraft(response, timezone);

  if (REQUIRED_FISCAL_DOCUMENT_FIELDS.some((field) => draft[field] === null)) {
    return null;
  }

  return {
    fiscalDocumentNumber: draft.fiscalDocumentNumber as number,
    fiscalDocumentSign: draft.fiscalDocumentSign as string,
    fiscalDriveNumber: draft.fiscalDriveNumber as string,
    fiscalizedAt: draft.fiscalizedAt as string,
    receiptNumber: draft.receiptNumber as number,
    receiptUrl: draft.receiptUrl,
    shiftNumber: draft.shiftNumber as number,
  };
}

/**
 * Обязательные реквизиты резолвятся в порядке «явное поле ответа → значение из QR».
 * Ни одного источника — по-прежнему fail-closed: поле остаётся `null`.
 */
export function extractKkmFiscalDocumentDraft(response: KkmServerResponse, timezone?: string): KkmFiscalDocumentDraft {
  const raw = response.raw;
  // Расширенный ответ отдаёт все реквизиты явными полями — тогда разбор QR (с двумя
  // разборами даты) не нужен вовсе. `??` не вычисляет правую часть, если левая есть.
  const qr = lazyFiscalQrCode(raw, timezone);

  return {
    fiscalDocumentNumber:
      positiveIntegerField(raw, 'DocumentNumber', 'FiscalDocumentNumber') ?? qr()?.fiscalDocumentNumber ?? null,
    fiscalDocumentSign: stringField(raw, 'FiscalSign', 'FiscalDocumentSign') ?? qr()?.fiscalDocumentSign ?? null,
    fiscalDriveNumber:
      stringField(raw, 'FN_Number', 'FnNumber', 'FiscalDriveNumber') ?? qr()?.fiscalDriveNumber ?? null,
    fiscalizedAt:
      explicitFiscalizedAt(stringField(raw, 'DateTimeKKT', 'FiscalizedAt'), timezone) ?? qr()?.fiscalizedAt ?? null,
    receiptNumber: positiveIntegerField(raw, 'CheckNumber', 'ReceiptNumber'),
    receiptUrl: receiptUrlField(raw),
    shiftNumber: positiveIntegerField(raw, 'SessionNumber', 'ShiftNumber'),
  };
}

function lazyFiscalQrCode(
  raw: Readonly<Record<string, unknown>>,
  timezone: string | undefined,
): () => KkmFiscalQrCode | null {
  let parsed: KkmFiscalQrCode | null | undefined;

  return () => {
    if (parsed === undefined) {
      const value = stringField(raw, 'QRCode', 'QrCode');
      parsed = value === null ? null : parseKkmFiscalQrCode(value, timezone);
    }

    return parsed;
  };
}

/**
 * `QRCode` в этот список не входит: это payload ФНС, а не ссылка на чек. Пока он тут
 * стоял первым, фильтр `^https?://` молча занулял `receiptUrl` даже при переданном `URL`.
 */
function receiptUrlField(raw: Readonly<Record<string, unknown>>): string | null {
  const value = stringField(raw, 'URL', 'ReceiptURL', 'ReceiptUrl');

  return value !== null && /^https?:\/\//i.test(value) ? value : null;
}

function qrFields(value: string): Map<string, string> | null {
  const fields = new Map<string, string>();

  for (const pair of value.trim().split('&')) {
    const separator = pair.indexOf('=');

    if (separator <= 0) {
      return null;
    }

    const key = pair.slice(0, separator).trim().toLowerCase();
    const fieldValue = pair.slice(separator + 1).trim();

    if (key === '' || fieldValue === '' || fields.has(key)) {
      return null;
    }

    fields.set(key, fieldValue);
  }

  return fields;
}

/**
 * `t` записан местным временем ККТ. Используем зону неизменяемого снимка операции.
 * Без timezone местное время не интерпретируется.
 */
function qrFiscalizedAt(value: string | undefined, timezone: string | undefined): string | null {
  if (value === undefined) return null;
  const match = /^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})?$/.exec(value);
  if (!match) return null;
  return explicitFiscalizedAt(
    `${match[1]}-${match[2]}-${match[3]}T${match[4]}:${match[5]}:${match[6] ?? '00'}`,
    timezone,
  );
}

/** У ответа устройства ISO-оффсет имеет приоритет над зоной его снимка. */
export function parseKkmDateTime(value: string | null, timezone?: string): Temporal.Instant | null {
  if (value === null) return null;
  const source = /^\d{4}-\d{2}-\d{2}$/.test(value) ? `${value}T00:00` : value;
  const text = source.replace(' ', 'T').replace(/([+-]\d{2})(\d{2})$/, '$1:$2');
  return parseInstant(text) ?? parseLocalInstant(text, timezone);
}

function explicitFiscalizedAt(value: string | null, timezone: string | undefined): string | null {
  const instant = parseKkmDateTime(value, timezone);
  return instant ? serializeInstant(instant, 'millisecond') : null;
}

function qrDecimal(value: string): string | null {
  return /^[0-9]+(\.[0-9]{1,2})?$/.test(value) ? value : null;
}

/** Первый ключ-синоним с непустым строковым значением: кассы называют поля по-разному. */
function stringField(raw: Readonly<Record<string, unknown>>, ...keys: string[]): string | null {
  for (const key of keys) {
    const value = optionalString(raw[key]);

    if (value !== null) {
      return value;
    }
  }

  return null;
}

function positiveIntegerField(raw: Readonly<Record<string, unknown>>, ...keys: string[]): number | null {
  for (const key of keys) {
    const value = optionalPositiveInteger(raw[key]);

    if (value !== null) {
      return value;
    }
  }

  return null;
}
